import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  Typography,
  Box,
  TextField,
  CircularProgress,
  Chip,
  Snackbar,
  Alert,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import { AffectedOrder } from "../Domain/AffectedOrder";
import { MealOption } from "../Domain/MealSettings";
import Translate from "../../../react-shared/Translations";
import {
  groupOrdersByDate,
  categorizeDietPreferences,
} from "../utils/orderHelpers";

// Helper function for string formatting with placeholders
const formatString = (template: string, ...args: (string | number)[]): string => {
  return template.replace(/{(\d+)}/g, (match, index) => {
    const argIndex = parseInt(index, 10);
    return args[argIndex] !== undefined ? String(args[argIndex]) : match;
  });
};

// Helper function to format date in YYYY-MM-DD format using local timezone
const formatDateForInput = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

interface Props {
  open: boolean;
  orders: AffectedOrder[];
  mealOptions: MealOption[];
  canUnlockOrders: boolean;
  onConfirm: (lockDate: Date) => Promise<void>;
  onCancel: () => void;
}

const OrderUpdateConfirmationModal: React.FC<Props> = ({
  open,
  orders,
  mealOptions,
  canUnlockOrders,
  onConfirm,
  onCancel,
}) => {
  const [lockDate, setLockDate] = useState<Date>(new Date());
  const [ordersWithStatus, setOrdersWithStatus] = useState<AffectedOrder[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">("success");
  const [currentTime, setCurrentTime] = useState<Date>(new Date());
  const listRef = useRef<HTMLUListElement>(null);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setIsProcessing(false);
      setShowSnackbar(false);
      setSnackbarMessage("");
      setLockDate(new Date());
    }
  }, [open]);

  // Update locked status when lockDate or orders change
  useEffect(() => {
    const now = new Date();
    const updatedOrders = orders.map((order) => {
      // Compare dates using local date parts (not UTC) to avoid timezone issues
      const orderDateLocal = new Date(order.date.getFullYear(), order.date.getMonth(), order.date.getDate());
      const lockDateLocal = new Date(lockDate.getFullYear(), lockDate.getMonth(), lockDate.getDate());
      const dateComparison = orderDateLocal < lockDateLocal;

      // Determine if order is locked based on lockTime (if available)
      const isLockedByTime = order.lockTime ? now >= order.lockTime : order.isLocked;

      // Privileged users can override lock status with datepicker
      // Unprivileged users can lock more orders, but cannot unlock orders already locked by time/API
      const isOrderLocked = canUnlockOrders
        ? dateComparison  // Privileged: datepicker controls everything
        : (dateComparison || isLockedByTime);  // Unprivileged: date OR time-based lock

      return {
        ...order,
        isLocked: isOrderLocked,
        isProcessed: false,
      };
    });
    setOrdersWithStatus(updatedOrders);
  }, [orders, lockDate, canUnlockOrders]);


  // Update current time every second for countdown display
  // Skip countdown updates for elevated users - locks don't affect them
  useEffect(() => {
    if (!open || canUnlockOrders) return;

    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(interval);
  }, [open, canUnlockOrders]);

  const handleConfirm = async () => {
    setIsProcessing(true);

    const unlockedOrders = ordersWithStatus.filter((o) => !o.isLocked);
    const totalOrders = unlockedOrders.length;

    if (totalOrders === 0) {
      setIsProcessing(false);
      return;
    }

    try {
      // Make API call and wait for it to complete - this blocks until API responds
      await onConfirm(lockDate);

      // API succeeded - stop spinner and show success snackbar
      setIsProcessing(false);
      setSnackbarMessage(Translate("orders_updated_successfully"));
      setSnackbarSeverity("success");
      setShowSnackbar(true);
    } catch (error: any) {
      console.error("Failed to update orders:", error);

      // Stop the spinner
      setIsProcessing(false);

      // Extract error message from Axios error response
      let message = "An unexpected error occurred";
      if (error.response?.data?.error) {
        message = error.response.data.error;
      } else if (error.message) {
        message = error.message;
      }

      // Show error snackbar
      setSnackbarMessage(message);
      setSnackbarSeverity("error");
      setShowSnackbar(true);
    }
  };

  const handleCancel = () => {
    if (!isProcessing) {
      onCancel();
    }
  };

  const handleLockDateChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = new Date(event.target.value);
    if (!isNaN(newDate.getTime())) {
      setLockDate(newDate);
    }
  }, []);

  // Memoize grouped orders to avoid recalculation on every render
  const ordersByDate = useMemo(
    () => groupOrdersByDate(ordersWithStatus),
    [ordersWithStatus]
  );

  // Memoize unlocked count
  const unlockedCount = useMemo(
    () => ordersWithStatus.filter((o) => !o.isLocked).length,
    [ordersWithStatus]
  );

  // Find the date of the last locked order
  const lastLockedDate = useMemo(() => {
    const lockedOrders = ordersWithStatus.filter(o => o.isLocked);
    if (lockedOrders.length === 0) return null;

    // Find the maximum date among locked orders
    const maxDate = lockedOrders.reduce((max, order) => {
      return order.date > max ? order.date : max;
    }, lockedOrders[0].date);

    return maxDate;
  }, [ordersWithStatus]);

  // Get the day after the last locked date
  const dayAfterLastLocked = useMemo(() => {
    if (!lastLockedDate) return null;
    const nextDay = new Date(lastLockedDate);
    nextDay.setDate(nextDay.getDate() + 1);
    return nextDay;
  }, [lastLockedDate]);

  // Find the first unlocked order date (when no locked orders exist)
  const firstUnlockedOrderDate = useMemo(() => {
    if (lastLockedDate !== null) return null; // Only use when no locked orders

    const unlockedOrders = ordersWithStatus.filter(o => !o.isLocked && o.lockTime);
    if (unlockedOrders.length === 0) return null;

    // Find the minimum date among unlocked orders with lockTime
    const minDate = unlockedOrders.reduce((min, order) => {
      return order.date < min ? order.date : min;
    }, unlockedOrders[0].date);

    return minDate;
  }, [ordersWithStatus, lastLockedDate]);

  // Helper to check if order should show countdown
  const shouldShowCountdown = useCallback((order: AffectedOrder): boolean => {
    // Hide countdown for elevated users - locks don't affect them
    if (canUnlockOrders) return false;

    if (order.isLocked || !order.lockTime) return false;

    const timeUntil = order.lockTime.getTime() - currentTime.getTime();
    if (timeUntil <= 0) return false;

    // Determine target date based on whether there are locked orders
    let targetDate: Date | null = null;
    if (dayAfterLastLocked) {
      // If there are locked orders, show countdown on day after last locked
      targetDate = dayAfterLastLocked;
    } else if (firstUnlockedOrderDate) {
      // If no locked orders, show countdown on first unlocked order date
      targetDate = firstUnlockedOrderDate;
    } else {
      return false;
    }

    // Check if order is on the target date
    const orderDateStr = `${order.date.getFullYear()}-${String(order.date.getMonth() + 1).padStart(2, '0')}-${String(order.date.getDate()).padStart(2, '0')}`;
    const targetDateStr = `${targetDate.getFullYear()}-${String(targetDate.getMonth() + 1).padStart(2, '0')}-${String(targetDate.getDate()).padStart(2, '0')}`;

    return orderDateStr === targetDateStr;
  }, [currentTime, dayAfterLastLocked, firstUnlockedOrderDate, canUnlockOrders]);

  // Helper to format countdown
  const formatCountdown = (lockTime: Date): string => {
    const timeUntil = lockTime.getTime() - currentTime.getTime();
    if (timeUntil <= 0) return "";

    const hours = Math.floor(timeUntil / (1000 * 60 * 60));
    const minutes = Math.floor((timeUntil % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((timeUntil % (1000 * 60)) / 1000);

    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        // Prevent closing during processing
        if (isProcessing) {
          return;
        }
        handleCancel();
      }}
      maxWidth="lg"
      fullWidth
      disableEscapeKeyDown={isProcessing}
    >
      <DialogTitle>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="body1" fontWeight="bold">
            {Translate("Update_Orders_With_New_Diet_Settings")}
          </Typography>
          <Chip
            label="v4.4"
            size="small"
            sx={{
              backgroundColor: "#4caf50",
              color: "#ffffff",
              fontSize: "0.65rem",
              height: "18px",
              fontWeight: "bold"
            }}
          />
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="caption" display="block" gutterBottom>
          {Translate("The_following_orders_will_be_updated")}:
        </Typography>

        <Box sx={{ my: 2, ml: 0 }}>
          <TextField
            label={Translate("From_Date")}
            type="date"
            value={formatDateForInput(lockDate)}
            onChange={handleLockDateChange}
            disabled={isProcessing}
            size="small"
            InputLabelProps={{
              shrink: true,
            }}
            InputProps={{
              sx: {
                "& input[type='date']": {
                  textAlign: "left",
                  paddingLeft: "8px"
                }
              }
            }}
            FormHelperTextProps={{
              sx: {
                marginLeft: 0
              }
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  marginLeft: 0
                }
              },
              "& .MuiInputLabel-root": {
                marginLeft: 0
              }
            }}
            helperText={Translate("Orders_before_this_date_will_not_be_updated")}
          />
        </Box>

        <Box sx={{ ml: 0 }}>
          <Typography variant="caption" color="textSecondary">
            {ordersWithStatus.length - unlockedCount > 0
              ? formatString(
                  Translate("orders_will_be_updated_with_locked"),
                  unlockedCount,
                  ordersWithStatus.length - unlockedCount
                )
              : formatString(Translate("orders_will_be_updated"), unlockedCount)}
          </Typography>
        </Box>

        <List
          ref={listRef}
          sx={{
            maxHeight: "600px",
            overflow: "auto",
            border: "1px solid #ddd",
            borderRadius: 1,
          }}
        >
          {Object.keys(ordersByDate)
            .sort()
            .map((dateKey) => (
              <React.Fragment key={dateKey}>
                <ListItem sx={{ bgcolor: "#f5f5f5", py: 0.5, minHeight: "32px" }}>
                  <ListItemText
                    primary={
                      <Typography variant="caption" fontWeight="bold">
                        {new Date(dateKey).toLocaleDateString()}
                      </Typography>
                    }
                  />
                </ListItem>
                {ordersByDate[dateKey].map((order) => {
                  // For locked orders: show current diet preferences (unchanged + removed = what exists now)
                  // For unlocked orders: show all changes (added, removed, unchanged)
                  const allPrefs = categorizeDietPreferences(order, mealOptions);
                  const categorizedPrefs = order.isLocked
                    ? allPrefs
                        .filter(pref => pref.status === "unchanged" || pref.status === "removed")
                        .map(pref => ({ ...pref, status: "unchanged" as const })) // Force all to gray on locked rows
                    : allPrefs;

                  return (
                    <ListItem
                      key={order.id}
                      sx={{
                        pl: 4,
                        py: 0.5,
                        opacity: order.isLocked ? 0.4 : 1,
                        transition: "all 0.3s ease",
                      }}
                    >
                      <LockIcon
                        sx={{
                          mr: 1,
                          color: order.isLocked ? "text.disabled" : "transparent",
                          fontSize: 18,
                        }}
                      />
                      <ListItemText
                        primary={
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                            <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                              {order.name}
                            </Typography>
                            {shouldShowCountdown(order) && (
                              <Chip
                                label={`🔒 ${Translate("Locks_in")}: ${formatCountdown(order.lockTime!)}`}
                                size="small"
                                color="warning"
                                sx={{
                                  height: "20px",
                                  fontSize: "0.7rem",
                                  fontWeight: "bold",
                                }}
                              />
                            )}
                            {categorizedPrefs.map((pref, index) => (
                              <Chip
                                key={index}
                                label={pref.name}
                                size="small"
                                sx={{
                                  height: "20px",
                                  fontSize: "0.7rem",
                                  backgroundColor:
                                    pref.status === "added"
                                      ? "#4caf50"
                                      : pref.status === "removed"
                                      ? "#f44336"
                                      : "#9e9e9e",
                                  color: "#ffffff",
                                }}
                              />
                            ))}
                          </Box>
                        }
                      />
                    </ListItem>
                  );
                })}
              </React.Fragment>
            ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleCancel}
          disabled={isProcessing}
          variant="contained"
          size="small"
        >
          {Translate("Close")}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="primary"
          size="small"
          disabled={isProcessing || unlockedCount === 0}
          startIcon={isProcessing ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {isProcessing
            ? `${Translate("Updating")}...`
            : Translate("Confirm_Update")}
        </Button>
      </DialogActions>

      {/* Snackbar for success/error notifications */}
      <Snackbar
        open={showSnackbar}
        autoHideDuration={4000}
        onClose={() => setShowSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ maxWidth: '400px' }}
      >
        <Alert
          onClose={() => setShowSnackbar(false)}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default OrderUpdateConfirmationModal;
